import { useState, useCallback, useEffect } from 'react';
import useSWR, { mutate } from 'swr';
import {
  listUsers,
  listGroups,
  listUsersInGroup,
  updateGroupMembers,
  createUser,
  disableUser,
  enableUser,
  CognitoUser,
  GroupOption,
} from '@/api/adminClient';
import { getErrorMessage } from '@/utils/error';

interface UseUserManagementResult {
  // データ
  allUsers: CognitoUser[];
  groups: GroupOption[];
  selectedGroup: string | null;
  groupMembers: CognitoUser[];
  nonGroupMembers: CognitoUser[];

  // ローディング状態
  isLoadingUsers: boolean;
  isLoadingGroups: boolean;
  isLoadingMembers: boolean;
  isUpdating: boolean;

  // エラー
  error: string | null;

  // 選択状態
  selectedNonMembers: string[];
  selectedMembers: string[];

  // アクション
  setSelectedGroup: (group: string | null) => void;
  setSelectedNonMembers: (usernames: string[]) => void;
  setSelectedMembers: (usernames: string[]) => void;
  addSelectedToGroup: () => Promise<boolean>;
  removeSelectedFromGroup: () => Promise<boolean>;
  createNewUser: (email: string) => Promise<boolean>;
  disableExistingUser: (username: string) => Promise<boolean>;
  enableExistingUser: (username: string) => Promise<boolean>;
  refresh: () => void;
}

// SWRフェッチャー
const usersFetcher = async () => {
  const result = await listUsers();
  return result.users;
};

const groupsFetcher = async () => {
  const result = await listGroups();
  return result.groups;
};

const membersFetcher = async (groupName: string) => {
  const result = await listUsersInGroup(groupName);
  return result.users;
};

/**
 * ユーザー管理フック
 * 既存Sugino APIを利用
 */
export function useUserManagement(): UseUserManagementResult {
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [selectedNonMembers, setSelectedNonMembers] = useState<string[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 全ユーザー取得
  const {
    data: allUsers,
    error: usersError,
    isLoading: isLoadingUsers,
  } = useSWR<CognitoUser[]>('admin-users', usersFetcher, {
    revalidateOnFocus: false,
  });

  // グループ一覧取得
  const {
    data: groups,
    error: groupsError,
    isLoading: isLoadingGroups,
  } = useSWR<GroupOption[]>('admin-groups', groupsFetcher, {
    revalidateOnFocus: false,
  });

  // 選択中グループのメンバー取得
  const {
    data: groupMembers,
    error: membersError,
    isLoading: isLoadingMembers,
  } = useSWR<CognitoUser[]>(
    selectedGroup ? `admin-group-members-${selectedGroup}` : null,
    () => membersFetcher(selectedGroup!),
    { revalidateOnFocus: false }
  );

  // グループに入っていないユーザーを計算
  const nonGroupMembers = (allUsers || []).filter(
    user => !(groupMembers || []).some(m => m.username === user.username)
  );

  // グループ変更時に選択をクリア
  useEffect(() => {
    setSelectedNonMembers([]);
    setSelectedMembers([]);
  }, [selectedGroup]);

  // エラーを集約
  useEffect(() => {
    const err = (usersError && getErrorMessage(usersError)) ||
                (groupsError && getErrorMessage(groupsError)) ||
                (membersError && getErrorMessage(membersError));
    setError(err || null);
  }, [usersError, groupsError, membersError]);

  // 選択したユーザーをグループに追加
  const addSelectedToGroup = useCallback(async (): Promise<boolean> => {
    if (!selectedGroup || selectedNonMembers.length === 0) return false;

    setIsUpdating(true);
    setError(null);

    try {
      const result = await updateGroupMembers({
        groupName: selectedGroup,
        addUsers: selectedNonMembers,
      });

      if (result.success) {
        // キャッシュを更新
        mutate(`admin-group-members-${selectedGroup}`);
        setSelectedNonMembers([]);
        return true;
      } else {
        throw new Error(result.error || '追加に失敗しました');
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, '不明なエラーが発生しました'));
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [selectedGroup, selectedNonMembers]);

  // 選択したユーザーをグループから削除
  const removeSelectedFromGroup = useCallback(async (): Promise<boolean> => {
    if (!selectedGroup || selectedMembers.length === 0) return false;

    setIsUpdating(true);
    setError(null);

    try {
      const result = await updateGroupMembers({
        groupName: selectedGroup,
        removeUsers: selectedMembers,
      });

      if (result.success) {
        // キャッシュを更新
        mutate(`admin-group-members-${selectedGroup}`);
        setSelectedMembers([]);
        return true;
      } else {
        throw new Error(result.error || '削除に失敗しました');
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, '不明なエラーが発生しました'));
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [selectedGroup, selectedMembers]);

  // ユーザー新規作成
  const createNewUser = useCallback(async (email: string): Promise<boolean> => {
    setIsUpdating(true);
    setError(null);

    try {
      await createUser(email);
      mutate('admin-users');
      return true;
    } catch (err: unknown) {
      setError(getErrorMessage(err, '不明なエラーが発生しました'));
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  // ユーザー無効化
  const disableExistingUser = useCallback(async (username: string): Promise<boolean> => {
    setIsUpdating(true);
    setError(null);

    try {
      await disableUser(username);
      mutate('admin-users');
      return true;
    } catch (err: unknown) {
      setError(getErrorMessage(err, '不明なエラーが発生しました'));
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  // ユーザー有効化
  const enableExistingUser = useCallback(async (username: string): Promise<boolean> => {
    setIsUpdating(true);
    setError(null);

    try {
      await enableUser(username);
      mutate('admin-users');
      return true;
    } catch (err: unknown) {
      setError(getErrorMessage(err, '不明なエラーが発生しました'));
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  // 全データ再取得
  const refresh = useCallback(() => {
    mutate('admin-users');
    mutate('admin-groups');
    if (selectedGroup) {
      mutate(`admin-group-members-${selectedGroup}`);
    }
  }, [selectedGroup]);

  return {
    allUsers: allUsers || [],
    groups: groups || [],
    selectedGroup,
    groupMembers: groupMembers || [],
    nonGroupMembers,
    isLoadingUsers,
    isLoadingGroups,
    isLoadingMembers,
    isUpdating,
    error,
    selectedNonMembers,
    selectedMembers,
    setSelectedGroup,
    setSelectedNonMembers,
    setSelectedMembers,
    addSelectedToGroup,
    removeSelectedFromGroup,
    createNewUser,
    disableExistingUser,
    enableExistingUser,
    refresh,
  };
}

export default useUserManagement;
